import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Copy, Check, ExternalLink, Rocket, Flame, Wallet, Layers,
  AlertTriangle, ChevronDown,
} from "lucide-react";
import { buildSolscanUrl } from "./solscan";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shortAddr = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

const ROLE_ICON = {
  ecosystem:  Rocket,
  buyback:    Flame,
  operations: Layers,
};

function CopyBtn({ value, small }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      className={`sticky-transp-copy ${small ? "is-small" : ""} ${copied ? "is-copied" : ""}`}
      onClick={onCopy}
      data-testid={`transp-copy-${(value || "").slice(0, 6)}`}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function TreasuryCard({ role, name, percent, amountUsd, address, purpose, network = "MAINNET" }) {
  const Icon = ROLE_ICON[role] || Wallet;
  const solscan = address ? buildSolscanUrl("address", address, network) : null;
  return (
    <article className="sticky-transp-card" data-testid={`transp-treasury-${role}`}>
      <header className="sticky-transp-card-head">
        <span className="sticky-transp-card-icon"><Icon size={16} strokeWidth={2.4} /></span>
        <div>
          <div className="sticky-transp-card-name">{name}</div>
          <div className="sticky-transp-card-role">{percent}% · ${amountUsd.toFixed(2)} per $99 launch</div>
        </div>
      </header>
      <p className="sticky-transp-card-purpose">{purpose}</p>
      <div className="sticky-transp-card-addr">
        <code data-testid={`transp-addr-${role}`}>{address || "Not configured"}</code>
      </div>
      <div className="sticky-transp-card-actions">
        <CopyBtn value={address || ""} />
        {solscan ? (
          <a
            href={solscan}
            target="_blank"
            rel="noopener noreferrer"
            className="sticky-transp-solscan"
            data-testid={`transp-solscan-${role}`}
          >
            <ExternalLink size={11} /> View on Solscan
          </a>
        ) : (
          <span className="sticky-transp-solscan is-disabled">Solscan unavailable</span>
        )}
      </div>
    </article>
  );
}

/**
 * Homepage / Launchpad shared consumer. Fetches /api/launchpad/transparency
 * once and exposes the payload via a callback for children that want a
 * hero + description panel.
 */
export function useTransparencyData() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/launchpad/transparency`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setErr("");
    } catch (e) { setErr(String(e?.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { data, err, reload: load };
}

/* -------------------------------------------------------------
 * Homepage TransparencySection (Team Wallet + 3 treasuries)
 * ----------------------------------------------------------- */
export function TransparencySection({ teamWalletAddress, teamWalletActive }) {
  const { data, err } = useTransparencyData();
  const [open, setOpen] = useState(false);
  const wallets = data?.treasuries || [];
  const walletCount = 1 + wallets.length; // Team + treasuries

  return (
    <section id="transparency" className="sticky-transp-section" data-testid="transp-section">
      <header className="sticky-transp-head">
        <span className="sticky-transp-badge"><ShieldCheck size={12} /> TRANSPARENCY</span>
        <h2 className="sticky-transp-h2">Public <span className="sticky-accent">Wallets</span></h2>
        <p className="sticky-transp-lede">
          Every Launchpad wallet is publicly viewable on Solscan. Team Wallet remains a separate 3% allocation and is not part of the 40/40/20 Launchpad fee split.
        </p>
      </header>

      {err && (
        <div className="sticky-transp-error" role="alert" data-testid="transp-error">
          <AlertTriangle size={13} /> Unable to load transparency data: {err}
        </div>
      )}

      <button
        type="button"
        className={`sticky-transp-toggle ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="transp-wallets-panel"
        data-testid="transp-toggle"
      >
        <span className="sticky-transp-toggle-left">
          <Wallet size={15} strokeWidth={2.4} />
          <span>
            <strong>View Public Wallets</strong>
            <small>{walletCount} verifiable addresses on Solscan</small>
          </span>
        </span>
        <ChevronDown className="sticky-transp-toggle-chev" size={18} strokeWidth={2.4} />
      </button>

      <div
        id="transp-wallets-panel"
        className={`sticky-transp-collapse ${open ? "is-open" : ""}`}
        data-testid="transp-collapse"
        aria-hidden={!open}
      >
        <div className="sticky-transp-collapse-inner">
          <div className="sticky-transp-grid" data-testid="transp-grid">
            {/* Team Wallet card (separate concept from Launchpad treasuries) */}
            <article className="sticky-transp-card is-team" data-testid="transp-team">
              <header className="sticky-transp-card-head">
                <span className="sticky-transp-card-icon"><Wallet size={16} strokeWidth={2.4} /></span>
                <div>
                  <div className="sticky-transp-card-name">Team Wallet</div>
                  <div className="sticky-transp-card-role">3% allocation · outside Launchpad fee</div>
                </div>
              </header>
              <p className="sticky-transp-card-purpose">
                Fixed 3% supply reserved for the core team. Wallet is public and fully verifiable on Solscan.
              </p>
              <div className="sticky-transp-card-addr">
                <code>{teamWalletActive ? teamWalletAddress : "Coming at launch"}</code>
              </div>
              <div className="sticky-transp-card-actions">
                {teamWalletActive ? (
                  <>
                    <CopyBtn value={teamWalletAddress} />
                    <a
                      href={buildSolscanUrl("address", teamWalletAddress, "MAINNET")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sticky-transp-solscan"
                    >
                      <ExternalLink size={11} /> View on Solscan
                    </a>
                  </>
                ) : (
                  <span className="sticky-transp-solscan is-disabled">Reveal at launch</span>
                )}
              </div>
            </article>

            {wallets.map((t) => (
              <TreasuryCard key={t.role} {...t} network="MAINNET" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------
 * Launchpad — "Launch Fee Transparency" section
 * ----------------------------------------------------------- */
export function LaunchFeeTransparencyPanel() {
  const { data, err } = useTransparencyData();
  const list = data?.treasuries || [];
  return (
    <section className="sticky-lp-card sticky-transp-launchpad" data-testid="transp-launchpad">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">Launch Fee <span className="sticky-accent">Transparency</span></h2>
        <span className="sticky-lp-help-inline">$99 · 40/40/20</span>
      </div>
      <p className="sticky-transp-lede">
        Every $99 Launch Fee is split server-side into three public treasuries. Copy any address or view it on Solscan to verify balances in real time.
      </p>
      {err && (
        <div className="sticky-lp-error" role="alert"><AlertTriangle size={13} /> {err}</div>
      )}
      <div className="sticky-transp-grid is-condensed">
        {list.map((t) => <TreasuryCard key={t.role} {...t} network="MAINNET" />)}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------
 * Buy & Burn card — Launchpad (Explore or hero)
 * ----------------------------------------------------------- */
export function BuyBurnPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/launchpad/burns`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { setErr(String(e?.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const status = data?.status || "PENDING_STICKY_LAUNCH";
  const burns  = data?.burns || [];
  const pending = status === "PENDING_STICKY_LAUNCH";

  return (
    <section className="sticky-lp-card sticky-transp-buyburn" data-testid="lp-buyburn-panel">
      <div className="sticky-lp-card-head">
        <h2 className="sticky-lp-h2">Buy &amp; <span className="sticky-accent">Burn</span></h2>
        <span className={`sticky-transp-status ${pending ? "is-pending" : "is-armed"}`} data-testid="lp-buyburn-status">
          <Flame size={11} /> {status}
        </span>
      </div>

      {pending ? (
        <div className="sticky-transp-buyburn-empty" data-testid="lp-buyburn-empty">
          <Flame size={22} />
          <h3>STICKY has not launched yet.</h3>
          <p>
            Buy &amp; Burn is currently <b>PENDING_STICKY_LAUNCH</b>. Once STICKY launches, the Buyback Treasury will use accumulated fees to buy STICKY and perform real on-chain burns — each burn will appear here with a verifiable Solscan link.
          </p>
        </div>
      ) : (
        <>
          <div className="sticky-transp-buyburn-stats">
            <div><span>Total STICKY Burned</span><b>{(data?.totalBurned || 0).toLocaleString("en-US")}</b></div>
            <div><span>Burn Transactions</span><b>{data?.count || 0}</b></div>
          </div>
          <ul className="sticky-transp-burnlist" data-testid="lp-burn-list">
            {burns.map((b) => (
              <li key={b.burnId} className="sticky-transp-burn-row">
                <div>
                  <div className="sticky-transp-burn-date">{new Date(b.timestamp).toLocaleString()}</div>
                  <div className="sticky-transp-burn-amount">
                    Burned <b>{Number(b.amount).toLocaleString("en-US")}</b> STICKY
                  </div>
                </div>
                {/* Solscan button ONLY when a real signature exists */}
                {b.transactionSignature && (
                  <a
                    href={buildSolscanUrl("tx", b.transactionSignature, b.network || "MAINNET")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sticky-transp-solscan"
                    data-testid={`lp-burn-solscan-${b.burnId}`}
                  >
                    <ExternalLink size={11} /> View on Solscan
                  </a>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {err && (
        <p className="sticky-lp-note" style={{ color: "#F87171" }}>Unable to load burn history: {err}</p>
      )}
    </section>
  );
}
